/**
 * Convert a view spec (keys array) into a template format for ViewEditorPage.
 * 
 * This module handles the conversion from view spec format (keys array)
 * to frontend template format (with depends_on and parent_options).
 * 
 * process:
 * 1. Process parent keys (main keys, non-child) and build submenu mapping
 * 2. Process child keys (referenced as submenus) and group them by name
 * 3. Return combined array of parent and child fields
 */

const normalizeText = (text) => text === "" ? "-" : text;

// Extract the default value text from a key's default option.
// Returns empty string if no default is set or the option is not found.
const getDefaultText = (key) => {
  if (!key.default) return "";
  const defaultText = key.options?.find(o => o.id === key.default)?.text || "";
  return normalizeText(defaultText);
};

/**
 * Create a parent select key in the template format from an original key.
 * converts empty text to "-", and extracts symbology (color) and submenu relationships.
 */
function createParentSelectField(key) {
  return {
    name: key.name,
    title: key.title || key.name,
    type: "select",
    options: key.options ? key.options.map(opt => ({
      text: normalizeText(opt.text),
      originalId: opt.id,
      parent_options: {},
      symbology: opt.color ? { "[]": { color: opt.color } } : {},
      submenus: opt.submenus || []
    })) : [],
    depends_on: "",
    default: getDefaultText(key)
  };
}

/**
 * Create a parent non-select field (number, textarea, text)
 * Handles type-specific properties (min/max for numbers) and ensures all values
 * are properly converted to strings.
 */
function createParentNonSelectField(key) {
  const base = {
    name: key.name,
    title: key.title || key.name,
    type: key.type,
    default: key.default || ""
  };
  
  if (key.type === "number") {
    return {
      ...base,
      min: key.min?.toString() || "0",
      max: key.max?.toString() || "0"
    };
  }
  
  return base;
}

/**
 * Process all parent fields and build the submenu-to-parent mapping.
 * Identifies parent fields and add them, and builds a map from submenu 
 * IDs to their parent field/option.
 * This mapping is essential for correctly associating child fields 
 * with their parent options in the second pass.
 */
function processParentFields(keys) {
  const parentFields = [];
  const submenuToParentOptionMap = {};
  
  keys.forEach(key => {
    const isParentField = !key.id || key.id === key.name;
    if (!isParentField) return;
    
    if (key.type === "select") {
      const parentField = createParentSelectField(key);
      parentFields.push(parentField);
      
      // Build mapping from submenu IDs to parent field/option
      parentField.options.forEach(opt => {
        if (opt.submenus?.length > 0) {
          opt.submenus.forEach(submenuId => {
            submenuToParentOptionMap[submenuId] = {
              parentField: parentField,
              parentOption: opt
            };
          });
        }
      });
    } else if (["number", "textarea", "text"].includes(key.type)) {
      parentFields.push(createParentNonSelectField(key));
    }
  });
  
  return { parentFields, submenuToParentOptionMap };
}

/**
 * Add an option from a child key into a child field's options array.
 * Since child keys are grouped by name, multiple child keys can contribute options
 * to the same child field. If an option with the same text already exists (from another
 * child key with the same name), we merge the parent_options to track all parent options
 * that lead to this option.
 */
function mergeChildOption(childField, opt, parentOption) {
  const optText = normalizeText(opt.text);
  const existingOpt = childField.options.find(o => o.text === optText);
  
  if (!existingOpt) {
    // New option: add it
    childField.options.push({
      text: optText,
      parent_options: { [parentOption.text]: true },
      symbology: opt.color ? { "[]": { color: opt.color } } : {}
    });
  } else {
    // Option already exists: merge parent_options (same option can come from multiple child keys)
    existingOpt.parent_options[parentOption.text] = true;
    if (opt.color && !existingOpt.symbology?.["[]"]?.color) {
      existingOpt.symbology = { "[]": { color: opt.color } };
    }
  }
}

/**
 * Get or create a child field in the map, grouped by name
 * Multiple child keys can have the same name but different IDs ("fissures-sousfamille"
 * and "humdite-sousfamille" both have name "sousfamille"). This function ensures they are
 * merged into a single field object, with a warning if dependencies are inconsistent.
 */
function getOrCreateChildField(childFieldsMap, key, parentField) {
  if (!childFieldsMap[key.name]) {
    childFieldsMap[key.name] = {
      name: key.name,
      title: key.title || key.name,
      type: key.type,
      depends_on: parentField.name,
      options: [],
      default: ""
    };
  }
  
  const childField = childFieldsMap[key.name];
  if (childField.depends_on !== parentField.name) {
    console.warn(`Child field "${key.name}" with id "${key.id}" depends on "${parentField.name}" but another child field with the same name depends on "${childField.depends_on}". Using "${parentField.name}".`);
    childField.depends_on = parentField.name;
  }
  
  return childField;
}

/**
 * Set the default value for a child field from the key's default option.
 * Prefers non-empty defaults over empty ones ("-"). This ensures that when multiple
 * child keys with the same name have different defaults, the most meaningful one is chosen.
 */
function setChildFieldDefault(childField, key) {
  if (!key.default) return;
  const defaultOpt = key.options?.find(o => o.id === key.default);
  if (defaultOpt) {
    const defaultText = normalizeText(defaultOpt.text);
    if (!childField.default || (childField.default === "-" && defaultText !== "-")) {
      childField.default = defaultText;
    }
  }
}

/**
 * Process a child key using the submenu mapping
 * Uses the submenuToParentOptionMap to find which parent option this child key belongs to,
 * then merges its options into the appropriate child field
 */
function processChildKeyWithMapping(key, parentInfo, childFieldsMap) {
  const { parentField, parentOption } = parentInfo;
  const childField = getOrCreateChildField(childFieldsMap, key, parentField);
  
  if (key.options) {
    key.options.forEach(opt => mergeChildOption(childField, opt, parentOption));
  }
  
  setChildFieldDefault(childField, key);
}

/**
 * Process all child fields from the keys array, grouping them by name.
 * A child field is identified by being listed as a submenu
 */
function processChildFields(keys, parentFields, submenuToParentOptionMap) {
  const childFieldsMap = {};
  
  keys.forEach(key => {
    // A child field is one that is referenced as a submenu in at least one parent option
    // A child field MUST have an id (it's referenced by id in parent options' submenus)
    const parentInfo = submenuToParentOptionMap[key.id];
    
    if (parentInfo) {
      // This key is a child field (referenced as submenu in at least one parent option)
      processChildKeyWithMapping(key, parentInfo, childFieldsMap);
    }
    // If not in mapping, it's not a child field (no fallback - children must have id and be in mapping)
  });
  
  return childFieldsMap;
}

/**
 * Convert a view spec (keys array) into a template format for ViewEditorPage.
 * Processes all parent keys and builds the submenu mapping, then processes child keys
 * (grouped by name), and finally combines them into a single array.
 */
export function convertViewSpecToTemplate(keys) {
  const { parentFields, submenuToParentOptionMap } = processParentFields(keys);
  const childFieldsMap = processChildFields(keys, parentFields, submenuToParentOptionMap);
  
  return [...parentFields, ...Object.values(childFieldsMap)];
}

